import { useQuery, useMutation } from "react-query";

import { invalidateQuery } from "@/api";

import type { Survey } from "../types";
import * as api from "../api";
import { useHasSurveysEntitlement } from "./useHasSurveysEntitlement";
import type { CreateSurveyParams, UpdateSurveyParams } from "../api";

export default function useSurveyStore() {
  const { hasEntitlement } = useHasSurveysEntitlement();
  const { data: surveys = [], isLoading } = useQuery(
    ["get_surveys"],
    api.fetchSurveys,
    { enabled: hasEntitlement },
  );

  const createMutation = useMutation(
    (data: CreateSurveyParams) => api.createSurvey(data),
    {
      onSuccess: () => {
        void invalidateQuery(["get_surveys"]);
      },
    },
  );

  const updateMutation = useMutation(
    ({ surveyId, data }: { surveyId: string; data: UpdateSurveyParams }) =>
      api.updateSurvey(surveyId, data),
    {
      onSuccess: () => {
        void invalidateQuery(["get_surveys"]);
      },
    },
  );

  const deleteMutation = useMutation(
    (surveyId: string) => api.deleteSurvey(surveyId),
    {
      onSuccess: () => {
        void invalidateQuery(["get_surveys"]);
      },
    },
  );

  const publishMutation = useMutation(
    (surveyId: string) => api.publishSurvey(surveyId),
    {
      onSuccess: () => {
        void invalidateQuery(["get_surveys"]);
      },
    },
  );

  const archiveMutation = useMutation(
    (surveyId: string) => api.archiveSurvey(surveyId),
    {
      onSuccess: () => {
        void invalidateQuery(["get_surveys"]);
      },
    },
  );

  const unpublishMutation = useMutation(
    (surveyId: string) => api.unpublishSurvey(surveyId),
    {
      onSuccess: () => {
        void invalidateQuery(["get_surveys"]);
      },
    },
  );

  return {
    surveys,
    isLoading,
    getSurvey: (id: string) => surveys.find((s) => s.id === id),
    createSurvey: async (
      draft: Omit<
        Survey,
        "id" | "createdAt" | "updatedAt" | "responseCount" | "status"
      >,
    ): Promise<Survey> => {
      return createMutation.mutateAsync(draft);
    },
    updateSurvey: async (
      id: string,
      updates: Partial<Survey>,
    ): Promise<void> => {
      await updateMutation.mutateAsync({ surveyId: id, data: updates });
    },
    deleteSurvey: async (id: string): Promise<void> => {
      await deleteMutation.mutateAsync(id);
    },
    publishSurvey: async (id: string): Promise<void> => {
      await publishMutation.mutateAsync(id);
    },
    archiveSurvey: async (id: string): Promise<void> => {
      await archiveMutation.mutateAsync(id);
    },
    unpublishSurvey: async (id: string): Promise<void> => {
      await unpublishMutation.mutateAsync(id);
    },
  };
}
